import { CommonModule } from '@angular/common'
import type { OnInit } from '@angular/core'
import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from '@angular/core'

import { BlobPlayerDirective } from '@app/directives/blob-player.directive'
import { ConversationStore } from '@app/modules/messaging/components/store/conversation.store'
import type { IconSymbol } from '@app/modules/ui/components/icon/icon.model'
import { UIModule } from '@app/modules/ui/ui.module'
import { MemberService } from '@app/services/member.service'
import { TrackService } from '@app/services/track.service'

import { VoiceMessageRecorderStore } from './voice-message-recorder.store'
import { FieldBoostComponent } from '../../../boost/components/field-boost/field-boost.component'

type VoiceMessageCreatorStatus = 'pristine' | 'recording' | 'uploaded' | 'playing' | 'paused'

@Component({
  selector: 'sb-voice-message-recorder',
  templateUrl: './voice-message-recorder.component.html',
  styleUrls: ['./voice-message-recorder.component.scss'],
  imports: [CommonModule, UIModule, BlobPlayerDirective, FieldBoostComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoiceMessageRecorderComponent implements OnInit {
  private readonly trackService = inject(TrackService)
  readonly voiceMessageRecorderStore = inject(VoiceMessageRecorderStore)
  readonly conversationStore = inject(ConversationStore)
  readonly memberService = inject(MemberService)

  readonly player = viewChild(BlobPlayerDirective)

  readonly status = computed<VoiceMessageCreatorStatus>(() => {
    const player = this.player()
    if (!player) {
      return this.voiceMessageRecorderStore.recordingEndsAt() === undefined ? 'pristine' : 'recording'
    }
    if (player.playing()) {
      return 'playing'
    }
    return player.currentTime() === 0 ? 'uploaded' : 'paused'
  })

  readonly ACTION_CONFIG = {
    pristine: {
      title: 'Record Voice Message',
      icon: 'mic-on-fill',
      color: {
        type: 'gradient',
        name: 'blue',
      },
      onAction: () => {
        this.voiceMessageRecorderStore.startRecording()
        this.trackEvent('record')
      },
    },
    recording: {
      title: 'Recording...',
      icon: 'stop',
      color: {
        type: 'gradient',
        name: 'red',
      },
      onAction: () => this.voiceMessageRecorderStore.stopRecording(),
    },
    uploaded: {
      title: 'Ready to Send',
      icon: 'play-circle',
      color: {
        type: 'gradient',
        name: 'green',
      },
      onAction: () => {
        this.player()!.play()
        this.trackEvent('play')
      },
    },
    playing: {
      title: 'Playing...',
      icon: 'pause-2',
      color: {
        type: 'color',
        name: 'iron',
      },
      onAction: () => this.player()!.pause(),
    },
    paused: {
      title: 'Paused',
      icon: 'play-circle',
      color: {
        type: 'gradient',
        name: 'green',
      },
      onAction: () => this.player()!.play(),
    },
  } as const satisfies Record<
    VoiceMessageCreatorStatus,
    {
      title: string
      icon: IconSymbol
      color:
        | {
            type: 'gradient'
            name: 'blue' | 'red' | 'green'
          }
        | {
            type: 'color'
            name: 'iron'
          }
      onAction: () => void
    }
  >

  ngOnInit(): void {
    this.trackEvent('open_voice_recorder')
  }

  attemptToSendMessage(): void {
    const voice = this.conversationStore.voice()

    if (voice) {
      this.voiceMessageRecorderStore.attemptToSendMessage(voice, this.voiceMessageRecorderStore.boosted())
    }
  }

  private trackEvent(action: 'open_voice_recorder' | 'play' | 'record'): void {
    this.trackService.track('voice_message', {
      action,
      flow: this.conversationStore.voiceFlow()!,
      source: this.conversationStore.source,
    })
  }
}
